import { Box, createTheme, PaletteColor, Theme, ThemeProvider, useTheme } from "@mui/material";
import { ReactNode } from "react";

const getMacros = (theme: Theme) => {
  const {
    palette: {
      accents: { green, blueDark, orangeDark },
    },
  } = theme;

  const cho: PaletteColor = {
    lighter: green[50],
    light: green[100],
    main: green[700],
    dark: green[900],
    contrastText: "#fff",
  };

  const pro: PaletteColor = {
    lighter: blueDark[50],
    light: blueDark[100],
    main: blueDark[700],
    dark: blueDark[900],
    contrastText: "#fff",
  };

  const fat: PaletteColor = {
    lighter: orangeDark[50],
    light: orangeDark[100],
    main: orangeDark[700],
    dark: orangeDark[900],
    contrastText: "#fff",
  };

  return { cho, pro, fat };
};

const getLikeDislike = (theme: Theme) => {
  const {
    palette: { success, error },
  } = theme;

  const like = success;
  const dislike = error;

  return { like, dislike };
};

// MUI's stock Checkbox icons (CheckBoxOutlineBlank/CheckBox) are Material Design
// glyphs, which don't match this design system's checkbox: a plain 18x18 box with a
// border when unchecked, a solid fill with a checkmark when checked. Rendered directly
// from the design's own spec/asset rather than approximated with a generic icon.
// These use currentColor rather than a fixed palette value so MuiCheckbox's
// styleOverrides.root below can drive unchecked/checked/indeterminate/disabled color
// from one place (state precedence, color prop, disabled override) instead of the
// icon baking in a single color no matter what state or color prop is passed.
const CheckboxUncheckedIcon = () => (
  <Box sx={{ width: 18, height: 18, bgcolor: "common.white", border: "1px solid", borderColor: "currentColor", borderRadius: "3px" }} />
);

const CheckboxCheckedIcon = () => (
  <Box sx={{ width: 18, height: 18, bgcolor: "currentColor", borderRadius: "3px" }}>
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.2604 5.87213C13.5799 6.20163 13.5799 6.73587 13.2604 7.06537L8.35127 12.1279C8.03175 12.4574 7.5137 12.4574 7.19419 12.1279L4.73964 9.59662C4.42012 9.26712 4.42012 8.73288 4.73964 8.40338C5.05916 8.07387 5.5772 8.07387 5.89672 8.40338L7.77273 10.338L12.1033 5.87213C12.4228 5.54262 12.9408 5.54262 13.2604 5.87213Z"
        fill="white"
      />
    </svg>
  </Box>
);

const CheckboxIndeterminateIcon = () => (
  <Box
    sx={{
      width: 18,
      height: 18,
      bgcolor: "currentColor",
      borderRadius: "3px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box sx={{ width: 10, height: 2, bgcolor: "common.white", borderRadius: "1px" }} />
  </Box>
);

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const {
    palette: { greyscale, success, warning, info, error },
  } = theme;
  const teamworksCalendarEvent = greyscale[300];
  const rpeLight = success;
  const rpeModerate = warning;
  const rpeHeavy = error;
  const orderStatusNew = info;
  const orderStatusCooking = warning;
  const orderStatusDone = success;
  const orderStatusCancelled = error;

  const { cho, pro, fat } = getMacros(theme);
  const { like, dislike } = getLikeDislike(theme);

  const nutritionPalette = {
    cho,
    pro,
    fat,
    like,
    dislike,
    teamworksCalendarEvent,
    rpeLight,
    rpeModerate,
    rpeHeavy,
    orderStatusNew,
    orderStatusCooking,
    orderStatusDone,
    orderStatusCancelled,
  };

  const extendedTheme = createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      ...nutritionPalette,
    },
    components: {
      ...theme.components,
      // @teamworksdev/mui's own default of variant="contained" (the standard black
      // button, white text) stopped reaching the final merged theme after bumping
      // @teamworksdev/mui + @mui/material together - restore it here so every
      // <Button> across the app that doesn't pass an explicit variant keeps its intended look.
      MuiButton: {
        ...theme.components?.MuiButton,
        defaultProps: {
          ...theme.components?.MuiButton?.defaultProps,
          variant: "contained",
        },
      },
      // the separator under Tabs (Teams/Tags/
      // Transfers/Goals etc.) relies on this border-width/style pair from @teamworksdev/mui;
      // only the color half survived the theme merge, so the border became invisible (0px).
      MuiTabs: {
        ...theme.components?.MuiTabs,
        styleOverrides: {
          ...theme.components?.MuiTabs?.styleOverrides,
          root: {
            ...(theme.components?.MuiTabs?.styleOverrides?.root as object),
            borderBottomWidth: "2px",
            borderBottomStyle: "solid",
          },
        },
      },
      // labels should always sit above their field
      // (shrink: true), not start as inline placeholder text that animates up on focus.
      MuiInputLabel: {
        ...theme.components?.MuiInputLabel,
        defaultProps: {
          ...theme.components?.MuiInputLabel?.defaultProps,
          shrink: true,
        },
      },
      // MUI's outlined border normally carves a gap ("notch") sized to the label text, meant
      // to be filled by the label sitting in it. Since labels here always render above the
      // field (shrink: true) instead, that notch is never filled and just shows as a blank
      // segment of the border. notched: false stops the gap from being carved out at all.
      MuiOutlinedInput: {
        ...theme.components?.MuiOutlinedInput,
        defaultProps: {
          ...theme.components?.MuiOutlinedInput?.defaultProps,
          notched: false,
        },
      },
      // without this, MuiDialogTitle falls back to plain
      // Typography h6 sizing (24px/700 here) instead of the design system's own dialog-title
      // size/weight.
      MuiDialogTitle: {
        ...theme.components?.MuiDialogTitle,
        defaultProps: {
          ...theme.components?.MuiDialogTitle?.defaultProps,
          component: "h3",
        },
        styleOverrides: {
          ...theme.components?.MuiDialogTitle?.styleOverrides,
          root: {
            ...(theme.components?.MuiDialogTitle?.styleOverrides?.root as object),
            fontSize: 20,
            fontWeight: 600,
            lineHeight: "28px",
          },
        },
      },
      // MUI's DialogActions has an 8px padding baked into its
      // own base styles (unaffected by any of this); this 16px margin stacks on top of that to
      // produce the intended 24px of space around the button row. The 8px gap BETWEEN buttons
      // comes from a separate MuiDialogActions-spacing rule, untouched by this override.
      MuiDialogActions: {
        ...theme.components?.MuiDialogActions,
        styleOverrides: {
          ...theme.components?.MuiDialogActions?.styleOverrides,
          root: {
            ...(theme.components?.MuiDialogActions?.styleOverrides?.root as object),
            justifyContent: "flex-end",
            margin: "16px",
          },
        },
      },
      // MUI's own default Tab minWidth (90px) forces every tab
      // to be at least that wide, so short labels (e.g. the Foods/Recipes/Branded selector) end
      // up centered in extra empty space that reads as inflated left-right padding. minWidth: 10
      // lets tabs size to their actual content instead.
      MuiTab: {
        ...theme.components?.MuiTab,
        styleOverrides: {
          ...theme.components?.MuiTab?.styleOverrides,
          root: {
            ...(theme.components?.MuiTab?.styleOverrides?.root as object),
            minWidth: 10,
            "&.Mui-selected": {
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.87)",
            },
            "&:hover": {
              color: "rgba(0, 0, 0, 0.87)",
            },
          },
        },
      },
      // this design system's own fontSize scale for icons
      // (e.g. large: 24px) stopped applying, so any icon using a named size like
      // fontSize="large" falls back to MUI's own native, much larger size (35px) instead.
      MuiSvgIcon: {
        ...theme.components?.MuiSvgIcon,
        variants: [
          ...(theme.components?.MuiSvgIcon?.variants ?? []),
          { props: { fontSize: "extraExtraExtraLarge" }, style: { fontSize: 48 } },
          { props: { fontSize: "extraExtraLarge" }, style: { fontSize: 40 } },
          { props: { fontSize: "extraLarge" }, style: { fontSize: 32 } },
          { props: { fontSize: "large" }, style: { fontSize: 24 } },
          { props: { fontSize: "medium" }, style: { fontSize: 20 } },
          { props: { fontSize: "small" }, style: { fontSize: 16 } },
          { props: { fontSize: "extraSmall" }, style: { fontSize: 12 } },
        ],
      },
      // @teamworksdev/mui's own Checkbox/Radio styles (zero root padding, icon sized
      // off ownerState.size) stopped reaching the final merged theme after bumping
      // @teamworksdev/mui + @mui/material together, so checkboxes/radios fell back to
      // MUI's native, larger touch-target padding - which reads as too much gap between
      // the control and its label (per the design: 18px control, 8px gap, no extra
      // padding). Restoring the vendor's own values here rather than guessing new ones.
      // The Figma "Checkbox Base"/"Radio Base" components render at the "Large" (18px)
      // size variant, not MUI's native "medium" default - default to that so call sites
      // that don't pass an explicit size prop still land on 18px, not 16px.
      MuiCheckbox: {
        ...theme.components?.MuiCheckbox,
        defaultProps: {
          ...theme.components?.MuiCheckbox?.defaultProps,
          size: "large",
          icon: <CheckboxUncheckedIcon />,
          checkedIcon: <CheckboxCheckedIcon />,
          indeterminateIcon: <CheckboxIndeterminateIcon />,
        },
        styleOverrides: {
          ...theme.components?.MuiCheckbox?.styleOverrides,
          // padding: 0 keeps the clickable root tight to the 18x18 icon above. color
          // drives the icons' currentColor fill/border - disabled takes precedence
          // over checked/indeterminate so a disabled-checked box still reads as
          // greyscale[300], not the active color.
          root: ({ theme: { palette: { greyscale, error, info } }, ownerState: { color } }: any) => ({
            padding: 0,
            color: greyscale[500],
            "&.Mui-checked:not(.Mui-disabled)": { color: color === "error" ? error.main : info.main },
            "&.MuiCheckbox-indeterminate:not(.Mui-disabled)": { color: color === "error" ? error.main : info.main },
            "&.Mui-disabled": { color: greyscale[300] },
          }),
        },
      },
      MuiRadio: {
        ...theme.components?.MuiRadio,
        defaultProps: {
          ...theme.components?.MuiRadio?.defaultProps,
          size: "large",
        },
        styleOverrides: {
          ...theme.components?.MuiRadio?.styleOverrides,
          root: ({ theme: { palette: { greyscale, error } }, ownerState: { size, color } }: any) => ({
            padding: 0,
            "&.Mui-disabled .MuiSvgIcon-root": { color: greyscale[300] },
            "&:not(.Mui-checked):not(.Mui-disabled) .MuiSvgIcon-root": { color: color === "error" ? error.main : greyscale[500] },
            "& .MuiSvgIcon-root": { fontSize: ({ small: 14, medium: 16, large: 18, xlarge: 24 } as Record<string, number>)[size ?? ""] ?? 16 },
          }),
        },
      },
      // MUI's stock marginLeft: -11px on FormControlLabel exists to counteract the
      // control's own ~9px default padding so its visible mark still lines up with
      // sibling elements; with that padding now 0 (see MuiCheckbox/MuiRadio above),
      // there's nothing left to counteract, and no gap was ever inserted in its place.
      // gap: 8px matches the design directly instead of relying on leftover padding math.
      MuiFormControlLabel: {
        ...theme.components?.MuiFormControlLabel,
        styleOverrides: {
          ...theme.components?.MuiFormControlLabel?.styleOverrides,
          root: {
            ...(theme.components?.MuiFormControlLabel?.styleOverrides?.root as object),
            marginLeft: 0,
            gap: "8px",
          },
        },
      },
    },
  });

  return <ThemeProvider theme={extendedTheme}>{children}</ThemeProvider>;
}
